import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Play, ShoppingBag, Sparkles, ArrowRight, Tv, Music, BookOpen, GraduationCap, ExternalLink, Heart, Share2, X, Leaf, Brain, Gamepad2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import DarkPanSection from '@/components/DarkPanSection';
import SponsorBand from '@/components/SponsorBand';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const brands = [
  { name: "The Who Cares News™", icon: Tv, description: "Satirical newscasts that report on what matters... or doesn't.", link: "https://youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V&si=PobVwdyM2w5GqSQT" },
  { name: "GradTrax", icon: Music, description: "60+ original songs organically created with AI tools.", link: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EwxLHHytTn17kdp69kZBNvn" },
  { name: "WTFairytale", icon: BookOpen, description: "Fairytale adaptations with a twist.", link: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EwRgkLIlru3qUNH_7XQs8kf" },
  { name: "Social Science", icon: GraduationCap, description: "Sociology courses flipping the script.", link: "https://www.youtube.com/playlist?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7" },
];

const tickerItems = [
  "THE WHO CARES NEWS™",
  "GRAD P FAWKES™",
  "MGN1.6™",
  "WTFAIRYTALE",
  "GRADTRAX",
  "COCOALIFE™",
  "400+ VIDEOS",
  "60+ ORIGINAL TRACKS",
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [newsPosts, setNewsPosts] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    const fetchFeaturedContent = async () => {
      try {
        const [productsRes, videosRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/videos`)
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 3));
        setFeaturedVideos(videosRes.data.slice(0, 3));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching featured content:', error);
      }
    };

    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setNewsPosts(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching news:', error);
      }
    };

    fetchFeaturedContent();
    fetchNews();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Forest Creatures Mailing List Banner */}
      <div className="w-full bg-primary/90 border-b border-white/10 py-2 px-4">
        <div className="container-custom flex items-center justify-center gap-3">
          <Leaf className="w-4 h-4 text-accent" />
          <a 
            href="#forest-creatures-signup" 
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('forest-creatures-signup')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-white text-sm font-medium hover:text-accent transition-colors flex items-center gap-2"
            data-testid="forest-creatures-signup-link"
          >
            <span>Join the Forest Creatures</span>
            <span className="hidden sm:inline text-white/70">— the MGN1.6 mailing list</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/25 via-background to-background" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute -top-20 right-0 w-[520px] h-[520px] bg-secondary/10 rounded-full blur-[140px]" />

        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div initial="initial" animate="animate" variants={stagger} className="lg:col-span-7">
              <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-8">
                <button
                  type="button"
                  onClick={() => setShowVideoModal(true)}
                  className="group relative shrink-0 rounded-2xl bg-card/60 border border-border/60 p-1.5 hover:border-secondary/60 transition-colors"
                  aria-label="Watch the Grad P Fawkes intro video"
                  data-testid="hero-fox-emblem"
                >
                  <img
                    src="/grad-fox.png"
                    alt="Grad P Fawkes fox emblem"
                    className="w-16 h-16 md:w-20 md:h-20 object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-secondary flex items-center justify-center shadow-lg">
                    <Play className="w-3.5 h-3.5 text-white ml-0.5" />
                  </span>
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-accent font-bold">The Modern Gnostic 1.6™</p>
                  <p className="text-sm text-muted-foreground mt-1">Published sociologist · Recording artist · Author · Satirist</p>
                </div>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="hero-title"
              >
                <span className="text-foreground">Grad P</span>{' '}
                <span className="text-secondary">Fawkes™</span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
                Sociology, mythology, music and satire — built for people who want nutrition that works,
                education that sticks, a sharper read on society, and stories worth remembering.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-12">
                <Link to="/videos">
                  <Button className="btn-primary h-13 px-8 text-base" data-testid="hero-watch-btn">
                    <Play className="w-5 h-5 mr-2" />
                    Watch the Library
                  </Button>
                </Link>
                <Link to="/publications">
                  <Button className="btn-outline h-13 px-8 text-base" data-testid="hero-publications-btn">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Read the Publications
                  </Button>
                </Link>
              </motion.div>

              <motion.dl variants={fadeInUp} className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-border/50">
                {[
                  ['400+', 'YouTube videos'],
                  ['60+', 'Original tracks'],
                  ['3', 'Albums & EPs'],
                  ['4th', 'Sociological paradigm'],
                ].map(([value, label]) => (
                  <div key={label}>
                    <dt className="text-3xl md:text-4xl font-black text-secondary leading-none">{value}</dt>
                    <dd className="text-xs text-muted-foreground uppercase tracking-wider mt-2">{label}</dd>
                  </div>
                ))}
              </motion.dl>
            </motion.div>

            {/* Credentials panel */}
            <motion.aside
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="lg:col-span-5 rounded-3xl bg-card/70 backdrop-blur-xl border border-border/60 p-7 md:p-8"
              data-testid="hero-credentials"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-bold mb-5">Who you're working with</p>
              <ul className="space-y-4">
                {[
                  { icon: GraduationCap, title: 'BA, Social Science — Oregon State University', text: 'Communication & Media Technology. AAOT in Communications, COCC.' },
                  { icon: Brain, title: 'Perceptual Constructivism', text: 'Published work on the fourth sociological paradigm: capitalism, identity, cultural entropy.' },
                  { icon: Music, title: 'Spotify · Apple Music · Amazon Music', text: 'Organic + AI hybrid catalog: Wieża Ram (2025), ThornelodgeRoom (2020) and more.' },
                  { icon: BookOpen, title: 'Author of the Oumuamua series', text: 'Creator of the Tuwaqachi Universe — a multi-book mythic cosmology.' },
                  { icon: Tv, title: 'Host of The Who Cares News™', text: 'Satirical broadcast and the What the Fairy Tale adaptation series.' },
                ].map((item) => (
                  <li key={item.title} className="flex gap-4">
                    <span className="mt-0.5 w-9 h-9 shrink-0 rounded-xl bg-secondary/15 flex items-center justify-center">
                      <item.icon className="w-4.5 h-4.5 text-secondary" />
                    </span>
                    <div>
                      <p className="font-semibold text-sm leading-snug">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-sm text-secondary hover:underline" data-testid="hero-about-link">
                Full background <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* Find your path */}
      <section className="py-14 md:py-20 bg-muted/40 border-b border-border/30" data-testid="pathways-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-accent font-bold mb-2">Start here</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>What brought you in?</h2>
            </div>
            <p className="text-muted-foreground max-w-md text-sm md:text-base">Five doors into one body of work. Pick the one that fits and the menus above will take it from there.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { to: '/cocoalife', icon: Leaf, tag: 'Nutrition', title: 'CocoaLife™', text: 'A daily truffle with 102 minerals and 9 amino complexes. OSU capstone.', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
              { to: '/educators', icon: GraduationCap, tag: 'Education', title: 'Clear Lesson Studio', text: 'Ready-to-teach lesson packs and explainer videos for K-12 and beyond.', color: 'text-teal-400', bg: 'bg-teal-400/10' },
              { to: '/truth-is-verb', icon: Brain, tag: 'Sociology', title: 'Perceptual Constructivism', text: 'Essays, courses and the Who Cares News on power, myth and modern systems.', color: 'text-accent', bg: 'bg-accent/10' },
              { to: '/publications', icon: BookOpen, tag: 'Stories', title: 'Books & The Dark Pan', text: 'The Oumuamua series, the Tuwaqachi Universe and the Dark Pan saga.', color: 'text-secondary', bg: 'bg-secondary/10' },
              { to: '/games', icon: Gamepad2, tag: 'Games', title: 'Play', text: 'Anunnaki vs Humans and the Contract of Ascension quiz. Free in the browser.', color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
            ].map((p, i) => (
              <motion.div
                key={p.to}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
              >
                <Link
                  to={p.to}
                  className="group flex h-full flex-col rounded-2xl bg-card border border-border/50 p-6 hover:border-secondary/50 hover:-translate-y-1 transition-[transform,border-color] duration-300"
                  data-testid={`pathway-${p.tag.toLowerCase()}`}
                >
                  <span className={`w-11 h-11 rounded-xl ${p.bg} flex items-center justify-center mb-5`}>
                    <p.icon className={`w-5 h-5 ${p.color}`} />
                  </span>
                  <p className={`text-xs uppercase tracking-[0.2em] font-bold ${p.color}`}>{p.tag}</p>
                  <h3 className="font-bold text-lg mt-1 mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{p.text}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-foreground/80 group-hover:text-secondary transition-colors">
                    Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* Ticker */}
      <div className="py-4 bg-primary/20 border-y border-border/50">
        <Marquee speed={40} gradient={false}>
          {tickerItems.map((item, i) => (
            <span key={i} className="ticker-item">
              {item} <span className="text-secondary mx-4">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* The Who Cares News Header - Widescreen */}
      <section className="relative overflow-hidden">
        <div className="relative w-full" style={{ height: '40vh', minHeight: '300px' }}>
          {/* Background Image - Grad Fawkes & Genevieve News Desk */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/u0e9963i_cvr1.png')`,
              backgroundPosition: 'center 20%'
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          
          {/* Title */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center px-4"
            >
              <h2 
                className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-4"
                style={{ 
                  fontFamily: 'Playfair Display',
                  textShadow: '4px 4px 12px rgba(0,0,0,0.9), 0 0 60px rgba(217, 84, 12, 0.5)'
                }}
              >
                The Who Cares News™
              </h2>
              <p 
                className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto"
                style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.8)' }}
              >
                Apathetic Journalism for the Indifferent World
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Cares News Featured Video */}
      <section className="py-12 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 12, background: '#000' }}>
              <iframe 
                src="https://www.youtube.com/embed/videoseries?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V"
                title="The Who Cares News"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="who-cares-news-video"
              />
            </div>
            <p className="mt-4 text-center">
              <a 
                href="https://youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-secondary hover:underline flex items-center justify-center gap-2"
                data-testid="who-cares-playlist-link"
              >
                <Tv className="w-4 h-4" />
                Watch All Episodes on YouTube
                <ExternalLink className="w-4 h-4" />
              </a>
            </p>
          </div>
        </div>
      </section>

      <SponsorBand ids={['abracadabra', 'alex-vincent']} />

      {/* Brands Section - Moved Down */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-header mb-4">The Brands</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              Four creative universes, one published sociologist.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <a href={brand.link} target="_blank" rel="noopener noreferrer">
                  <Card className="bg-card border-border/50 hover:border-secondary/50 transition-all duration-300 h-full card-hover group">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-2xl bg-secondary/20 flex items-center justify-center mb-4">
                        <brand.icon className="w-7 h-7 text-secondary" />
                      </div>
                      <h3 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
                        {brand.name}
                        <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-secondary" />
                      </h3>
                      <p className="text-muted-foreground text-sm">{brand.description}</p>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Dark Pan - WTFairytale Project */}
      <DarkPanSection />

      {/* Featured Videos - Direct Playlist Links */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="section-header mb-2">Latest Content</h2>
              <p className="section-subheader">Fresh from the MGN1.6 studio</p>
            </div>
            <a href="https://youtube.com/@mgn1.6" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-2 text-secondary hover:underline">
              View Channel <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* The Who Cares News */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <a href="https://youtube.com/playlist?list=PLn8UbQXnz_ExF9R4UwNlqHAHF-d7OM24V&si=PobVwdyM2w5GqSQT" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/x2k008m7_cover.png"
                      alt="The Who Cares News"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent text-xs">News</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">The Who Cares News™</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">Satirical newscasts hosted by Grad Fawkes</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>

            {/* GradTrax Music */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_EwxLHHytTn17kdp69kZBNvn" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/5sugzb5b_openart-image_iJgwcJ2o_1767948939056_raw.jpg"
                      alt="GradTrax Music"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent text-xs">Music</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">GradTrax Music</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">60+ original songs and music videos</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>

            {/* WTFairytale */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <a href="https://www.youtube.com/playlist?list=PLn8UbQXnz_EwRgkLIlru3qUNH_7XQs8kf" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/tu22dhq0_WTF%20%282%29.png"
                      alt="WTFairytale"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent text-xs">Fairytale</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">WTFairytales</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">The Shocking Truth - Fairytale adaptations</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>

            {/* History of It All */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <a href="https://youtube.com/playlist?list=PLn8UbQXnz_EyD9w80jSJ-aQKTnHO0KKVt" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/47crv3os_history.png"
                      alt="History of It All"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent text-xs">History</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">History of It All</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">Before the clock was born</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>

            {/* Oumaumau Series */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <a href="https://youtube.com/playlist?list=PLn8UbQXnz_EygpbGPg3TA3s-MkO7GVqzU" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/ppx9y8eq_cover%20main.jpg"
                      alt="Oumaumau Series"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-1" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 badge-accent text-xs">Series</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">Oumaumau Series</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">Books, media & more</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>

            {/* The Last Laugh - Spotify Album */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <a href="https://open.spotify.com/album/5xjhxwFcNAS6dgkLPtM3Av?si=YpOogdJnSnSsONAr2R2kKA" target="_blank" rel="noopener noreferrer">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full">
                  <div className="relative aspect-video overflow-hidden bg-primary/20">
                    <img
                      src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/5urciyw7_1x1_a_brontosaurus_grazes_next_adam_%20-%20Copy.png"
                      alt="The Last Laugh - American History Lesson 101"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                        <Music className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <span className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">Spotify</span>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base mb-1 line-clamp-1">The Last Laugh</h3>
                    <p className="text-muted-foreground text-sm line-clamp-2">American History Lesson 101 - New Album</p>
                  </CardContent>
                </Card>
              </a>
            </motion.div>
          </div>

          <a href="https://youtube.com/@mgn1.6" target="_blank" rel="noopener noreferrer" className="md:hidden flex items-center justify-center gap-2 text-secondary mt-8 hover:underline">
            View All Videos <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <SponsorBand ids={['brkox', 'kingscamo', 'moonbike']} label="More partners" />

      {/* Publications & Initiatives */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-header mb-4">Publications & Initiatives</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              Support the mission, explore publications, and help make a difference
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {/* Publications */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Link to="/publications" data-testid="publications-link" className="block">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full cursor-pointer">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/bowuscbv_thumbnail-1773990327383.png" 
                        alt="Immigration and Policy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/fm3gp32a_thumbnail-1773996233914.png" 
                        alt="Myth, Music & Identity"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  <CardContent className="p-4 text-center">
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>Publications</h3>
                    <p className="text-muted-foreground text-sm">Rhythm of Society - Books & chapters</p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* PPC Framework - Truth Is a Verb */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <Link to="/truth-is-verb" data-testid="ppc-framework-link" className="block">
                <Card className="bg-[#0a0a0c] border-amber-300/30 overflow-hidden card-hover group h-full cursor-pointer">
                  <div className="aspect-[2/1] overflow-hidden bg-gradient-to-br from-amber-300/20 via-stone-900 to-stone-950 flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(252,211,77,0.1)_0%,_transparent_70%)]" />
                    <div className="text-center p-4 relative z-10">
                      <p className="text-xs tracking-[0.3em] uppercase text-amber-300 mb-2">PPC Framework</p>
                      <p className="text-2xl font-black text-stone-100">Truth Is a Verb</p>
                      <p className="text-amber-300 font-bold">You Bend</p>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center bg-[#0e0e10]">
                    <h3 className="text-lg font-bold mb-1 text-stone-100" style={{ fontFamily: 'Playfair Display' }}>PPC Framework</h3>
                    <p className="text-stone-400 text-sm">Philosophical Performance</p>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 mt-2"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open('https://youtube.com/playlist?list=PLn8UbQXnz_EwC1Iev4OTQrYme4XpY9_O7', '_blank', 'noopener'); }}
                      data-testid="ppc-youtube-link"
                    >
                      <Play className="w-3 h-3" /> Watch Playlist
                    </button>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* Stone Initiative */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/stone-initiative" data-testid="stone-initiative-link" className="block">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full cursor-pointer">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/2x2b1ha8_Copilot_20260320_163148.png" 
                        alt="The Global Stone Act"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/cys3oau9_st%202.png" 
                        alt="Stone Initiative"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  <CardContent className="p-4 text-center">
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>Stone Initiative</h3>
                    <p className="text-muted-foreground text-sm">The Global Stone Act</p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* Courses */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/courses" data-testid="courses-link" className="block">
                <Card className="bg-card border-border/50 overflow-hidden card-hover group h-full cursor-pointer">
                  <div className="aspect-[2/1] overflow-hidden bg-gradient-to-br from-primary/30 to-secondary/20 flex items-center justify-center">
                    <div className="text-center p-4">
                      <GraduationCap className="w-12 h-12 text-secondary mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Social Science & Sociology</p>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center">
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>Courses</h3>
                    <p className="text-muted-foreground text-sm">Learn from the expert</p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            {/* Donate */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/donate" data-testid="donate-link" className="block">
                <Card className="bg-card border-accent/50 overflow-hidden card-hover group h-full cursor-pointer" style={{ boxShadow: '0 0 30px rgba(244, 196, 48, 0.1)' }}>
                  <div className="aspect-[2/1] overflow-hidden bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center">
                    <div className="text-center p-4">
                      <Heart className="w-12 h-12 text-accent mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Support the mission</p>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center">
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>Donate</h3>
                    <p className="text-muted-foreground text-sm">Fund new content</p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Anunnaki vs Humans Game Widget */}
      <section className="py-16 md:py-24" id="anunnaki-game">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <a 
              href="/game" 
              className="block"
              data-testid="anunnaki-game-link"
            >
              <div className="relative rounded-2xl overflow-hidden card-hover group" style={{ boxShadow: '0 0 30px rgba(212, 175, 55, 0.4), 0 10px 40px rgba(0,0,0,0.5)' }}>
                <div 
                  className="h-72 bg-cover bg-center relative"
                  style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_bet-central-35/artifacts/2zsxb2go_8e1114_653e8c64b5db4657a19d5ac34ef1efe1~mv2%20-%20Copy.webp')" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
                  <div className="absolute inset-0 flex flex-col justify-center items-center p-6 text-center">
                    <h2 className="text-4xl font-bold mb-2" style={{ color: '#d4af37', fontFamily: 'Playfair Display', textShadow: '0 0 20px rgba(212, 175, 55, 0.5), 2px 2px 4px rgba(0,0,0,0.8)', letterSpacing: '3px' }}>
                      ANUNNAKI
                    </h2>
                    <h3 className="text-xl mb-4" style={{ color: '#22d3ee', textShadow: '0 0 15px rgba(34, 211, 238, 0.5), 1px 1px 3px rgba(0,0,0,0.8)', letterSpacing: '2px' }}>
                      VS HUMANS
                    </h3>
                    <p className="text-zinc-200 text-sm mb-5 max-w-md leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                      The ancient gods mine Earth's gold to save their dying planet. Humans fight for freedom. Choose your side.
                    </p>
                    <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-10 py-3 font-bold tracking-widest rounded group-hover:from-amber-400 group-hover:to-amber-500 transition-all" style={{ boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)' }}>
                      PLAY FREE NOW
                    </div>
                    <p className="text-cyan-400 text-xs mt-4 tracking-wider">BETA MODE • PRIZE COMING SOON</p>
                  </div>
                </div>
              </div>
            </a>
            <div className="flex justify-center gap-4 mt-4">
              <button 
                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/#anunnaki-game`)}
                className="text-xs text-muted-foreground hover:text-accent transition-colors flex items-center gap-1"
              >
                <Share2 className="w-3 h-3" />
                Copy Share Link
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CocoaLife™ CTA - MAIN FEATURED PRODUCT */}
      <section className="py-24 md:py-32 relative overflow-hidden truffle-section">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[120px]" />
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <span className="badge-accent mb-4 inline-block">Oregon State University Capstone Project</span>
            <h2 className="section-header mb-4">
              Introducing CocoaLife™
            </h2>
            <p className="section-subheader max-w-3xl mx-auto">
              A delicious chocolate truffle infused with 102 minerals and 9 amino acid complexes. 
              Your daily health supplement never tasted this good.
            </p>
          </div>

          {/* Video Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="video-wrapper glow-orange">
              <iframe
                src="https://www.youtube.com/embed/dIUBN4EcA_o"
                title="CocoaLife™ - Mission Statement & Discovery"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="text-center text-muted-foreground mt-4 text-sm">
              Watch: The Mission, Commercial & Discovery Behind CocoaLife™
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
                Health Meets Indulgence
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Developed as a capstone project at Oregon State University, CocoaLife™ combines 
                the rich, decadent taste of premium chocolate with a powerful blend of 102 trace minerals 
                and 9 essential amino acid complexes.
              </p>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                One truffle a day delivers comprehensive nutritional support while satisfying your 
                chocolate cravings. Finally, a health supplement you'll actually look forward to taking.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="px-4 py-2 bg-card rounded-full border border-border">
                  <span className="text-secondary font-bold">102</span> Minerals
                </div>
                <div className="px-4 py-2 bg-card rounded-full border border-border">
                  <span className="text-secondary font-bold">9</span> Amino Complexes
                </div>
                <div className="px-4 py-2 bg-card rounded-full border border-border">
                  <span className="text-secondary font-bold">OSU</span> Developed
                </div>
              </div>
              <Link to="/cocoalife">
                <Button className="btn-primary h-14 px-8 text-lg" data-testid="truffle-cta-btn">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Learn More & Order
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-square rounded-3xl overflow-hidden border border-border/50 glow-orange col-span-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/c4m7cv1f_Firefly_a%20beautify%20table%20with%20a%20fancy%20pl%2027140%2056A.jpg"
                  alt="CocoaLife™ Chocolate Truffle"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-border/50">
                <img
                  src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/9jyhfeo8_Firefly_a%20beautify%20table%20with%20fancy%20choc%2023026%20CZS.jpg"
                  alt="CocoaLife™ Gift Box"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden border border-border/50">
                <img
                  src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/zy1cz67w_Firefly_a%20beautify%20table%20with%20fancy%20choc%2047964%20TSN.jpg"
                  alt="CocoaLife™ Premium Box"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Oumaumau Book Series */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-accent mb-4 inline-block">Book Series - Available on Kindle</span>
            <h2 className="section-header mb-4">Oumaumau Series</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              A 6-book epic adventure series, each featuring original songs and albums.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:row-span-2"
            >
              <div className="sticky top-32">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-accent/30 glow-orange">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/pitb8dqp_cover%20main.jpg"
                    alt="Oumaumau and the Star Sung Compass - Book Cover"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center mt-4">
                  <p className="text-sm text-muted-foreground">Book 1 of 6</p>
                </div>
              </div>
            </motion.div>

            {/* Book 1 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <Card className="bg-card border-border/50 overflow-hidden h-full">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/videoseries?list=PLn8UbQXnz_EygpbGPg3TA3s-MkO7GVqzU"
                    title="Oumaumau and the Star Sung Compass - First 5 Chapters"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <CardContent className="p-6">
                  <span className="text-xs uppercase tracking-wider text-accent font-bold">Book 1 - Available Now</span>
                  <h3 className="text-2xl font-bold mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    Oumaumau and the Star Sung Compass
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    The first book in the epic 6-book series, featuring original songs and an accompanying album. 
                    Watch the first 5 chapters above!
                  </p>
                  <a 
                    href="https://a.co/d/07SUOsPj" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="btn-primary w-full">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Get on Amazon
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>

            {/* Book 2 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <Card className="bg-card border-secondary/30 overflow-hidden h-full" style={{ boxShadow: '0 0 30px rgba(217, 84, 12, 0.15)' }}>
                <div className="bg-secondary/20 text-center py-2 text-sm font-bold text-secondary">
                  COMING MAY 2026
                </div>
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/WWO1x55up6w"
                    title="Oumaumau and the Blue Flame - Theme Song"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <CardContent className="p-6">
                  <span className="text-xs uppercase tracking-wider text-secondary font-bold">Book 2 - Coming Soon</span>
                  <h3 className="text-2xl font-bold mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    Oumaumau and the Blue Flame
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    The second installment in the series. Listen to the theme song above! 
                    Releasing May 2026.
                  </p>
                  <Button className="btn-outline w-full" disabled>
                    Coming May 2026
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="section-header mb-2">Shop Merch</h2>
              <p className="section-subheader">Wear your indifference with pride</p>
            </div>
            <Link to="/shop" className="hidden md:flex items-center gap-2 text-secondary hover:underline">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/product/${product.id}`}>
                  <Card className="product-card group" data-testid={`featured-product-${product.id}`}>
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <CardContent className="p-5">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground capitalize">
                        {product.category}
                      </span>
                      <h3 className="font-bold text-lg mt-1 mb-2 line-clamp-1">{product.name}</h3>
                      <p className="text-secondary font-bold text-xl">${product.price.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          <Link to="/shop" className="md:hidden flex items-center justify-center gap-2 text-secondary mt-8 hover:underline">
            View All Products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Forest Creatures Mailing List Signup Section */}
      <section id="forest-creatures-signup" className="py-16 md:py-24 bg-gradient-to-b from-primary/10 via-background to-primary/5 relative overflow-hidden">
        {/* Decorative forest elements */}
        <div className="absolute top-10 left-10 text-6xl opacity-20">🌲</div>
        <div className="absolute top-20 right-20 text-5xl opacity-15">🦊</div>
        <div className="absolute bottom-10 left-1/4 text-4xl opacity-20">🦉</div>
        <div className="absolute bottom-20 right-10 text-5xl opacity-15">🌿</div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <span className="text-4xl mb-4 block">🦊</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Join the Forest Creatures
            </h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Subscribe to our mailing list for exclusive updates, early access to new releases, 
              behind-the-scenes content, and special offers from the MGN1.6 universe.
            </p>
            
            <div 
              className="max-w-md mx-auto p-8 rounded-xl border border-secondary/30 bg-secondary/5"
              data-testid="forest-creatures-coming-soon"
            >
              <p className="text-2xl font-bold text-secondary mb-2">🌲 Coming Soon 🌲</p>
              <p className="text-muted-foreground">
                Our mailing list sign-up is launching soon. Check back to join the Forest Creatures!
              </p>
            </div>
            
            <p className="text-xs text-muted-foreground mt-6">
              No spam, just magic from the forest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setShowVideoModal(false)}
            data-testid="video-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute -top-12 right-0 text-white hover:text-secondary transition-colors"
                data-testid="video-modal-close"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/CGjWySpxfNA?autoplay=1&rel=0&modestbranding=1"
                  title="Grad P Fawkes - Featured Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="text-center text-white/70 mt-4 text-sm">
                Click outside or press X to close
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
